import React from 'react';
import type { OperationsChecklist, StepStatus } from '@/types/operations';
import { formatDate, formatDateTime } from '@/utils/formatters';
import { ChecklistStatusBadge, STEP_STATUS_META } from './presentation';
import styles from './OperationsChecklist.module.css';

const COUNT_ORDER: StepStatus[] = ['PASSED', 'FAILED', 'PENDING', 'EXEMPTED'];

const COUNT_VALUE_CLASS: Record<StepStatus, string> = {
  PASSED:   styles['count-value-passed'],
  FAILED:   styles['count-value-failed'],
  PENDING:  styles['count-value-pending'],
  EXEMPTED: styles['count-value-exempted'],
};

/**
 * One line: which day, what state, and how the steps came out.
 *
 * Only non-zero counts are shown. Listing every status including the zeros made
 * four figures compete for attention when usually one or two carry the news —
 * and "0 Failed" is not news, while "2 Failed" is.
 */
const ChecklistHeader: React.FC<{ checklist: OperationsChecklist; actions?: React.ReactNode }> = ({
  checklist,
  actions,
}) => {
  const counts = COUNT_ORDER
    .map(status => ({ status, value: checklist.step_counts[status] ?? 0 }))
    .filter(c => c.value > 0);

  return (
    <div className={styles['checklist-meta']}>
      <span className={styles['checklist-date']}>{formatDate(checklist.checklist_date)}</span>
      <ChecklistStatusBadge status={checklist.status} />

      <span className={styles['count-row']}>
        {counts.map(({ status, value }) => (
          <span key={status} className={styles['count-item']}>
            <span className={`${styles['count-value']} ${COUNT_VALUE_CLASS[status]}`}>{value}</span>
            {STEP_STATUS_META[status].label.toLowerCase()}
          </span>
        ))}
        <span className={styles['count-total']}>of {checklist.total_steps} steps</span>
      </span>

      {checklist.status === 'COMPLETED' && (
        <span className={styles['completed-info']}>
          Completed by {checklist.completed_by_email || '—'}
          {checklist.completed_ts ? ` · ${formatDateTime(checklist.completed_ts)}` : ''}
        </span>
      )}

      {actions && <div className={styles['checklist-meta-actions']}>{actions}</div>}
    </div>
  );
};

export default ChecklistHeader;
